import React, { useState, useRef, useEffect } from "react";
import { FaPalette, FaEyeDropper } from "react-icons/fa";

const ColorPicker = ({
  type = "text",
  currentColor = "#000000",
  onColorChange,
  label = "Color",
  icon,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [customColor, setCustomColor] = useState(currentColor);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Predefined color palette
  const colorPalette = [
    // Basic colors
    "#000000",
    "#FFFFFF",
    "#FF0000",
    "#00FF00",
    "#0000FF",
    "#FFFF00",
    "#FF00FF",
    "#00FFFF",
    // Grays
    "#808080",
    "#C0C0C0",
    "#404040",
    "#606060",
    "#A0A0A0",
    "#E0E0E0",
    "#F0F0F0",
    "#F8F8F8",
    // Reds
    "#8B0000",
    "#DC143C",
    "#FF6347",
    "#FF4500",
    "#FF1493",
    "#FFB6C1",
    "#FFC0CB",
    "#FFCCCB",
    // Blues
    "#000080",
    "#0000CD",
    "#4169E1",
    "#1E90FF",
    "#87CEEB",
    "#87CEFA",
    "#B0E0E6",
    "#E6F3FF",
    // Greens
    "#006400",
    "#008000",
    "#32CD32",
    "#90EE90",
    "#98FB98",
    "#F0FFF0",
    "#ADFF2F",
    "#7CFC00",
    // Yellows/Oranges
    "#FFD700",
    "#FFA500",
    "#FF8C00",
    "#FFFFE0",
    "#FFFACD",
    "#FFEFD5",
    "#FFE4B5",
    "#FFDAB9",
    // Purples
    "#800080",
    "#9932CC",
    "#BA55D3",
    "#DA70D6",
    "#DDA0DD",
    "#E6E6FA",
    "#F8F8FF",
    "#9370DB",
    // Browns
    "#8B4513",
    "#A0522D",
    "#CD853F",
    "#D2B48C",
    "#F5DEB3",
    "#FAEBD7",
    "#FDF5E6",
    "#FFFAF0",
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleColorSelect = (color) => {
    setCustomColor(color);
    onColorChange(color);
    setIsOpen(false);
  };

  const handleCustomColorChange = (e) => {
    const color = e.target.value;
    setCustomColor(color);
    onColorChange(color);
  };

  const getTypeIcon = () => {
    switch (type) {
      case "background":
        return "🎨";
      case "highlight":
        return "🖍️";
      default:
        return "A";
    }
  };

  const getTypeLabel = () => {
    switch (type) {
      case "background":
        return "Background";
      case "highlight":
        return "Highlight";
      default:
        return "Text";
    }
  };

  return (
    <div className="relative inline-block">
      {/* Color Picker Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        title={`${getTypeLabel()} Color`}
        type="button"
      >
        <div className="flex items-center gap-1">
          <div
            className="w-4 h-4 rounded border border-gray-300 dark:border-gray-600 flex items-center justify-center text-xs font-bold"
            style={{
              backgroundColor: currentColor,
              color:
                type === "text"
                  ? currentColor
                  : currentColor === "#FFFFFF" || currentColor === "#ffffff"
                  ? "#000000"
                  : "#FFFFFF",
            }}
          >
            {type === "text" ? "A" : ""}
          </div>
          <span className="material-symbols-rounded !text-[16px]">{icon}</span>
        </div>
      </button>

      {/* Color Picker Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg p-3 z-50 min-w-[280px]"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {getTypeLabel()} Color
            </span>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              ✕
            </button>
          </div>

          {/* Quick Actions */}
          <div className="flex gap-2 mb-3">
            <button
              onClick={() => handleColorSelect("transparent")}
              className="px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
            >
              None
            </button>
            <button
              onClick={() => handleColorSelect("#000000")}
              className="px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
            >
              Default
            </button>
          </div>

          {/* Color Palette Grid */}
          <div className="grid grid-cols-8 gap-1 mb-3">
            {colorPalette.map((color, index) => (
              <button
                key={index}
                onClick={() => handleColorSelect(color)}
                className={`w-6 h-6 rounded border-2 hover:scale-110 transition-transform ${
                  currentColor === color
                    ? "border-blue-500 dark:border-blue-400"
                    : "border-gray-300 dark:border-gray-600"
                }`}
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>

          {/* Custom Color Input */}
          <div className="border-t border-gray-200 dark:border-gray-600 pt-3">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
              Custom Color
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={customColor}
                onChange={handleCustomColorChange}
                className="w-8 h-8 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
              />
              <input
                type="text"
                value={customColor}
                onChange={(e) => {
                  setCustomColor(e.target.value);
                  if (/^#[0-9A-F]{6}$/i.test(e.target.value)) {
                    onColorChange(e.target.value);
                  }
                }}
                placeholder="#000000"
                className="flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                pattern="^#[0-9A-F]{6}$"
              />
            </div>
          </div>

          {/* Recent Colors (if we want to add this feature later) */}
          {/* <div className="border-t border-gray-200 dark:border-gray-600 pt-3 mt-3">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
              Recent Colors
            </label>
            <div className="flex gap-1">
              // Recent colors would go here
            </div>
          </div> */}
        </div>
      )}
    </div>
  );
};

export default ColorPicker;
